import React, { useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import './parking-accordion.scss'; 

const CATEGORY_LABELS = {
  cs_street_surface_parking: 'Street & Surface Parking',
  cs_event_parking: 'Underground Parking Garage',
  cs_retail_valet_parking: 'Valet – Retail Location',
  cs_pac_valet_parking: 'Valet – PAC Location',
};

const CATEGORIES = Object.keys(CATEGORY_LABELS);

const hourlyRateCopy = 'Hourly rates apply at all other times';

function groupByDate(policies) {
  return policies.reduce((acc, node) => {
    const dateKey = moment(node.field_date.value).format('YYYY-MM-DD');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(node);
    return acc;
  }, {});
}

function ParkingAccordion({ policies }) {

  const today = moment().startOf('day');
  const endDate = moment(today).add(13, 'days');

  const grouped = useMemo(() => {
    const groupedByDate = groupByDate(
      policies.filter((node) => {
        const date = moment(node.field_date.value).startOf('day');
        return date.isSameOrAfter(today) && date.isSameOrBefore(endDate);
      })
    );

    const fullRange = Array.from({ length: 14 }).map((_, i) => {
      const date = moment(today).add(i, 'days');
      const key = date.format('YYYY-MM-DD');
      return {
        date,
        entries: groupedByDate[key] || [],
      };
    });

    return fullRange;
  }, [policies]);

  const [openIndex, setOpenIndex] = useState(null);
  const buttonRefs = useRef([]);

  const handleKeyDown = (e, index) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (index + 1) % buttonRefs.current.length;
      buttonRefs.current[next]?.focus();
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (index - 1 + buttonRefs.current.length) % buttonRefs.current.length;
      buttonRefs.current[prev]?.focus();
    }
  };

  return (
    <div className="parking-accordion">
      <a href="#accordion-end" className="sr-only">Skip to end of parking accordion</a>
      <h2 className="sr-only">Parking Policy Accordion for Upcoming Two Weeks</h2>
      {grouped.map(({ date, entries }, index) => {
        const displayDate = date.format('dddd, MMMM D');
        const weekday = date.format('dddd');
        const isOpen = openIndex === index;
        const buttonId = `accordion-button-${index}`;
        const panelId = `accordion-panel-${index}`;

        const policiesByType = CATEGORIES.reduce((acc, type) => {
          acc[type] = entries.filter((n) => n.field_policy_type === type);
          return acc;
        }, {});

        // scenarios
        // Street & Surface Parking is available every day with hourly rates
        // Underground Parking Garage is available every day with hourly rates unless there is an event
        // Valet Retail Location is not available on Sunday and Monday
        // Valet – Retail Location is available Tuesday through Saturday 5pm - 11pm with a rate of $10
        // Valet - Retail Location will display rate of $10 for times event rate is not shown i.e. 5pm - 6pm $10, 6pm - 10pm $20 event rate, 10pm - 11pm $10
        // Valet - Retail Location will display "Not Available" for times when valet is not in operation
        // Valet - PAC Location will display when there is an event and will have a rate of $20 unless changed.

        return (
          <div key={date.format('YYYY-MM-DD')} className={`accordion-day ${index === grouped.length - 1 ? 'last-day' : ''}`}>
            <h3>
              <button
                id={buttonId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="accordion-toggle"
                ref={(el) => buttonRefs.current[index] = el}
                onClick={() => setOpenIndex(isOpen ? null : index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
              >
                <span>{isOpen ? '−' : '+'}</span> {displayDate}
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              hidden={!isOpen}
              className="accordion-panel"
            >
              <h4 className="sr-only">Parking details for {displayDate}</h4>
              <div className="accordion-category">
                <h5>{CATEGORY_LABELS['cs_street_surface_parking']}</h5>
                <p>Hourly Rates</p>
              </div>

              <div className="accordion-category">
                <h5>{CATEGORY_LABELS['cs_event_parking']}</h5>
                {policiesByType['cs_event_parking'].length > 0 ? (
                  <div className='accordion-event-parking'>
                    <ul>
                      {policiesByType['cs_event_parking'].map((item, i) => {
                        const startTime = moment(item.field_date.value).format('h:mm A');
                        const endTime = moment(item.field_date.end_value).format('h:mm A');
                        const rate = item.field_rate === 0 ? 'Free Parking' : `$${item.field_rate}`;
                        return (
                          <li key={i}>
                            {startTime} – {endTime} – {item.field_rate === 0 ? rate : rate + ' event rate'}
                          </li>
                        );
                      })}
                    </ul>
                    <p>{hourlyRateCopy}</p>
                  </div>
                ) : (
                  <p>Hourly Rates</p>
                )}
              </div>

                {(() => {
                    const allValetEntries = policiesByType['cs_retail_valet_parking'];
                    const valetStart = moment(date).set({ hour: 17, minute: 0 });
                    const valetEnd = moment(date).set({ hour: 23, minute: 0 });

                    if (weekday === 'Sunday' || weekday === 'Monday') {
                      return (
                        <div className="accordion-category">
                          <h5>{CATEGORY_LABELS['cs_retail_valet_parking']}</h5>
                          <p>Closed</p>
                        </div>
                      );
                    }

                    const timeline = [];
                    let current = valetStart.clone();

                    const slices = allValetEntries.map((item) => ({
                      start: moment(item.field_date.value),
                      end: moment(item.field_date.end_value),
                      type: item.field_valet_not_in_operation ? 'block' : 'event',
                      rate: item?.field_rate,
                    })).sort((a, b) => a.start - b.start);

                    slices.forEach((slice) => {
                      if (slice.start.isAfter(current)) {
                        timeline.push({
                          start: current.clone(),
                          end: slice.start.clone(),
                          type: 'default',
                        });
                      }

                      timeline.push({
                        start: slice.start.clone(),
                        end: slice.end.clone(),
                        type: slice.type,
                        rate: slice.rate,
                      });

                      current = moment.max(current, slice.end);
                    });

                    if (current.isBefore(valetEnd)) {
                      timeline.push({
                        start: current,
                        end: valetEnd,
                        type: 'default',
                      });
                    }

                    return (
                      <div className="accordion-category">
                        <h5>{CATEGORY_LABELS['cs_retail_valet_parking']}</h5>
                        <ul>
                          {timeline.map((slot, i) => {
                            const start = slot.start.format('h:mm A');
                            const end = slot.end.format('h:mm A');

                            if (slot.type === 'event') {
                              return <li key={i}>{start} – {end} – ${slot.rate} event rate</li>;
                            }

                            if (slot.type === 'block') {
                              return <li key={i}>{start} – {end} – Not Available</li>;
                            }

                            return <li key={i}>{start} – {end} – $10</li>;
                          })}
                        </ul>
                      </div>
                    );
                  })()}

            
              {policiesByType['cs_pac_valet_parking'].length > 0 && (
                <div className="accordion-category">
                  <h5>{CATEGORY_LABELS['cs_pac_valet_parking']}</h5>
                  <ul>
                    {policiesByType['cs_pac_valet_parking'].map((item, i) => {
                      const startTime = moment(item.field_date.value).format('h:mm A');
                      const endTime = moment(item.field_date.end_value).format('h:mm A');
                      return (
                        <li key={i}>
                          {startTime} – {endTime} – ${item.field_rate} event rate
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div id="accordion-end" tabIndex="-1" />
    </div>
  );
}

ParkingAccordion.propTypes = {
  policies: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      field_policy_type: PropTypes.string,
      field_rate: PropTypes.number,
      field_date: PropTypes.shape({
        value: PropTypes.string,
        end_value: PropTypes.string,
      }),
    })
  ).isRequired,
};

export default ParkingAccordion;